import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import SearchBox from "@/components/feature/SearchBox";
import MediaCard from "@/components/feature/MediaCard";
import EmptyState from "@/components/feature/EmptyState";
import { getHotMedia, searchMedia } from "@/utils/media";

export default function SearchPage() {
  const [params] = useSearchParams();
  const keyword = (params.get("q") ?? "").trim();
  const results = useMemo(() => searchMedia(keyword), [keyword]);
  const hot = getHotMedia(6);

  useDocumentMeta({
    title: keyword
      ? `「${keyword}」搜索结果 - 亚洲一区二区在线`
      : "搜索影视 - 亚洲一区二区在线",
    description: keyword
      ? `在亚洲一区二区在线搜索「${keyword}」，为你找到相关亚洲电影、电视剧与综艺节目。`
      : "在亚洲一区二区在线搜索亚洲电影、电视剧与综艺节目。",
    keywords: `搜索影视,${keyword},亚洲电影,亚洲电视剧,亚洲一区二区在线`,
  });

  return (
    <div className="mx-auto w-full px-4 py-6 md:px-6 md:py-8">
      <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
        {keyword ? "搜索结果" : "搜索影视作品"}
      </h1>
      <p className="mt-1 text-sm text-foreground-500">
        {keyword
          ? `关键词：${keyword}`
          : "输入片名、导演、演员或类型，快速找到想看的亚洲影视内容"}
      </p>

      <div className="mt-5 max-w-xl">
        <SearchBox key={keyword} initialValue={keyword} placeholder="输入关键词后回车搜索" />
      </div>

      {keyword ? (
        <div className="mt-6">
          <p className="text-sm text-foreground-600">
            共找到{" "}
            <strong className="text-accent-600">{results.length}</strong> 个与「{keyword}
            」相关的影视内容
          </p>
          {results.length > 0 ? (
            <div className="mt-5 grid grid-cols-2 gap-3 md:gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {results.map((media) => (
                <MediaCard key={media.id} media={media} />
              ))}
            </div>
          ) : (
            <div className="mt-5">
              <EmptyState
                icon="ri-emotion-sad-line"
                title="未找到相关影视内容"
                description={`没有找到与「${keyword}」匹配的作品，试试更换关键词，或浏览下方热门推荐。`}
                actionLabel="返回首页"
                actionTo="/"
              />
              <div className="mt-10">
                <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-fire-line text-accent-500"></i>
                  热门影视推荐
                </h2>
                <div className="grid grid-cols-2 gap-3 md:gap-4 sm:grid-cols-3 lg:grid-cols-6">
                  {hot.map((media) => (
                    <MediaCard key={media.id} media={media} showSynopsis={false} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-10">
          <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <i className="ri-fire-line text-accent-500"></i>
            大家都在搜
          </h2>
          <div className="mb-6 flex flex-wrap gap-2">
            {["悬疑", "日韩电影", "古装剧", "综艺", "治愈", "2025新片"].map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="whitespace-nowrap rounded-full bg-background-100 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-background-200"
              >
                {tag}
              </Link>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 md:gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {hot.map((media) => (
              <MediaCard key={media.id} media={media} showSynopsis={false} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}